'use client';

/**
 * IntentMatchQualityCard — coherence stats + Pareto axis weights.
 *
 * Third of three rail cards mode3 splits the legacy `intent` Details
 * panel into. Renders sections 2 and 9 of `ENTITY_SCHEMA["intent"]`:
 * brief vs picks coherence (the gap flags off-brief drift), line
 * count, and the active Pareto axis weights with rationale prose.
 *
 * Composed from rail-native section primitives under
 * `rail-insights/sections/` — independent of the legacy
 * `copilot/details/sections/*` infrastructure that retires with
 * CopilotPanel. Same primitives `IntentBriefCard` consumes
 * (`<TextBlock>` for the rationale, `<StatsGrid>` for the header
 * numbers, `<AxisWeights>` for the weights list).
 *
 * Selection-aware: paints only when an Intent is the active viewport
 * selection. Shares the `intent_details` fetch with `IntentBriefCard`
 * via React Query dedup — one BFF call per selection across both
 * cards.
 *
 * @see klyde_edge/composition/cards/intent.py — content_key="intent_match_quality"
 *
 * @component_role: Card
 * @component_type: Rail Card
 * @domain: Copilot / Intent
 * @layer: presentation
 */

import { useMemo } from 'react';
import { Loader2, Activity } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCardExport } from '@/lib/design/card-export';
import type { IntentListResponse } from '@/lib/api/types';
import { RailCard } from '../rail-card';
import { StatsGrid } from '../sections/stats-grid';
import { AxisWeights, type AxisWeight } from '../sections/axis-weights';

const INTENT_DETAILS_STRATEGY = 'intent_details';

function pct(v: number | null | undefined): string | null {
  return typeof v === 'number' ? `${Math.round(v)}%` : null;
}

export function IntentMatchQualityCard() {
  const { entityType, entityId } = useViewportSelection();
  const enabled = entityType === 'intent' && !!entityId;

  const { data, isLoading, error } = useStrategyData<IntentListResponse>(
    INTENT_DETAILS_STRATEGY,
    enabled ? { intent_sid: entityId ?? '' } : {},
    enabled,
  );

  // Scene export — see IntentBriefCard for the doctrine. Shares the
  // `intent_details` query_key with the other two rail cards.
  useCardExport(
    useMemo(() => {
      const it = data?.data?.items?.[0];
      const status: 'loading' | 'loaded' | 'error' | 'idle' = !enabled
        ? 'idle'
        : isLoading
          ? 'loading'
          : error
            ? 'error'
            : it
              ? 'loaded'
              : 'idle';
      const briefPct = it?.coherence_pct ?? null;
      const picksPct =
        (it as unknown as { selection_coherence_pct?: number | null } | undefined)
          ?.selection_coherence_pct ?? null;
      const drift =
        typeof briefPct === 'number' && typeof picksPct === 'number'
          ? Math.round(briefPct - picksPct)
          : null;
      const axes = it?.pareto_axes ?? [];
      const driftNote =
        drift !== null && Math.abs(drift) >= 5
          ? drift > 0
            ? `Picks drifted ${Math.abs(drift)} pts off-brief.`
            : `Picks tightened ${Math.abs(drift)} pts vs brief.`
          : '';
      const stats = `Brief: ${pct(briefPct) ?? '—'} · Picks: ${pct(picksPct) ?? '—'} · Lines: ${it?.line_count ?? '—'}`;
      return {
        label: 'Match Quality',
        content_key: 'intent_match_quality',
        surface: 'rail' as const,
        status,
        query_key: enabled
          ? ['strategy-data', INTENT_DETAILS_STRATEGY, { intent_sid: entityId ?? '' }]
          : null,
        text: it ? [stats, driftNote].filter(Boolean).join('\n') : 'Match Quality',
        markdown: it
          ? [
              '## Match Quality',
              stats,
              driftNote,
              ...axes.map((a) => `- ${a.name}: ${a.weight}${a.changed ? ' (changed)' : ''}`),
            ]
              .filter(Boolean)
              .join('\n')
          : '## Match Quality',
        json: it
          ? {
              // Painted strings, matching the StatsGrid cells; the raw
              // numerics live in the payload. `drift` is an unpainted
              // intermediate — only its rendered note ships to scene.
              brief: pct(briefPct),
              picks: pct(picksPct),
              lines: it.line_count != null ? String(it.line_count) : null,
              drift_note: driftNote || null,
              axes: axes.map((a) => ({
                name: a.name,
                weight: a.weight,
                default: a.default,
                changed: a.changed,
              })),
              rationale: it.pareto_rationale ?? null,
            }
          : null,
      };
    }, [data, enabled, entityId, isLoading, error]),
  );

  if (!enabled) {
    return (
      <RailCard title="Match Quality" icon={Activity}>
        <div className="text-xs text-muted-foreground">Select an Intent to see match quality</div>
      </RailCard>
    );
  }

  if (isLoading) {
    return (
      <RailCard title="Match Quality" icon={Activity}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading match quality…
        </div>
      </RailCard>
    );
  }

  if (error || !data?.data) {
    return (
      <RailCard title="Match Quality" icon={Activity}>
        <div className="text-xs text-destructive">
          {error ? `Failed to load: ${(error as Error).message}` : 'No data'}
        </div>
      </RailCard>
    );
  }

  const intent = data.data.items?.[0];
  if (!intent) {
    return (
      <RailCard title="Match Quality" icon={Activity}>
        <div className="text-xs text-muted-foreground">No data for this Intent.</div>
      </RailCard>
    );
  }

  // `selection_coherence_pct` isn't on the IntentSummary type today —
  // it's exposed by the richer get_intent_detail response shape. Read
  // defensively via an unknown projection to avoid coupling to the
  // type-incomplete edge.
  const picksPct = (intent as unknown as { selection_coherence_pct?: number | null })
    .selection_coherence_pct ?? null;
  const briefPct = intent.coherence_pct ?? null;
  const drift =
    typeof briefPct === 'number' && typeof picksPct === 'number'
      ? Math.round(briefPct - picksPct)
      : null;

  const axes: AxisWeight[] = intent.pareto_axes ?? [];

  return (
    <RailCard title="Match Quality" icon={Activity}>
      <div className="space-y-3">
        <StatsGrid
          stats={[
            { label: 'Brief', value: pct(briefPct) },
            { label: 'Picks', value: pct(picksPct) },
            { label: 'Lines', value: intent.line_count ?? null },
          ]}
        />
        {drift !== null && Math.abs(drift) >= 5 && (
          <p className="text-[11px] text-warning">
            {drift > 0
              ? `Picks drifted ${Math.abs(drift)} pts off-brief.`
              : `Picks tightened ${Math.abs(drift)} pts vs brief.`}
          </p>
        )}
        <AxisWeights
          axes={axes}
          rationale={intent.pareto_rationale}
          label="Match weights"
        />
      </div>
    </RailCard>
  );
}
